import { Autocomplete, TextField } from "@mui/material";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import { useEffect, useState, useCallback } from "react";
import { Modal, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditButton } from "components/Buttons/EditButton";
import { DeleteButton } from "components/Buttons/DeleteButton";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import SubmitButton from "components/Buttons/SubmitButton";
import { showConfirm } from "utils/swalHelper";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SaveCancelButtons from "components/Buttons/SaveCancelButtons";

interface BagOption {
  inventory_item_id: string | number;
  bag_no: string;
  available_quantity: number;
}

interface BagRow {
  id: string;
  inventory_item_id: string | number;
  bag_no: string;
  available_quantity: number;
  issue_quantity: number;
}

interface BagWithoutId {
  inventory_item_id: string | number;
  bag_no: string;
  available_quantity: number;
  issue_quantity: number;
}

interface BagSelectionModalProps {
  show: boolean;
  tableFormik: any;
  handleClose: () => void;
  quantity: number;
  onSave: (bags: BagWithoutId[]) => void;
  initialBags?: BagWithoutId[];
  bagQuantityValidation?: Array<{
    inventory_item_id: string | number;
    available_quantity: number;
    issue_quantity: number;
  }>;
  setBagQuantityValidation?: (
    bags: Array<{
      inventory_item_id: string | number;
      available_quantity: number;
      issue_quantity: number;
    }>
  ) => void;
  editingItemId?: number | null;
  currentItemBags?: BagWithoutId[];
  editingRowData?: any;
  warehouseId?: string | number;
}

const QuotationBagSelectionModal: React.FC<BagSelectionModalProps> = ({
  show,
  tableFormik,
  handleClose,
  quantity,
  onSave,
  initialBags = [],
  bagQuantityValidation = [],
  setBagQuantityValidation,
  editingItemId = null,
  currentItemBags = [],
  editingRowData = null,
  warehouseId,
}) => {
  const [bagOptions, setBagOptions] = useState<BagOption[]>([]);
  const [rows, setRows] = useState<BagRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingBagId, setEditingBagId] = useState<string | null>(null);

  const fetchBagDropdown = useCallback(async () => {
    try {
      setLoading(true);
      // Use editing row data if editing, otherwise use tableFormik values
      const itemName =
        editingItemId !== null && editingRowData
          ? editingRowData?.item?.item_name
          : tableFormik?.values?.item?.item_name;
      const categoryName =
        editingItemId !== null && editingRowData
          ? editingRowData?.category?.category_name
          : tableFormik?.values?.category?.category_name;
      const subCategoryName =
        editingItemId !== null && editingRowData
          ? editingRowData?.sub_category?.sub_category_name
          : tableFormik?.values?.sub_category?.sub_category_name;

      const queryParams = new URLSearchParams({
        item_name: itemName || "",
        category_name: categoryName || "",
        sub_category_name: subCategoryName || "",
      });

      if (warehouseId) {
        queryParams.append("warehouse_id", String(warehouseId));
      }

      const result = await Axios.get(
        `${api?.warehouse?.item_inward?.bag_dropdown}?${queryParams.toString()}`
      );
      setBagOptions(result?.data?.data || []);
    } catch (err) {
      toast.error("Failed to fetch bag dropdown");
    } finally {
      setLoading(false);
    }
  }, [editingItemId, editingRowData, tableFormik, warehouseId]);

  useEffect(() => {
    if (show) {
      fetchBagDropdown();
      // Ensure all bags have an id field for modal internal use
      const bagsWithId: BagRow[] = (initialBags || []).map((bag, idx) => ({
        ...bag,
        id: `bag-${Date.now()}-${idx}`,
      }));
      setRows(bagsWithId);
      setEditingBagId(null);
      // Reset formik form when modal opens to clear any previous form data
      formik.resetForm();
    }
  }, [show, initialBags, fetchBagDropdown]);

  const validationSchema = Yup.object({
    bag_no: Yup.object().nullable().required("Bag No. is required"),
    available_quantity: Yup.number()
      .typeError("Available Quantity must be a number")
      .positive("Available Quantity must be greater than 0")
      .required("Available Quantity is required"),
    issue_quantity: Yup.number()
      .typeError("Issue Quantity must be a number")
      .positive("Issue Quantity must be greater than 0")
      .required("Issue Quantity is required")
      .test(
        "less-than-available",
        "Issue Quantity must be less than or equal to Available Quantity",
        function (value) {
          const availableQty = parseFloat(this.parent.available_quantity) || 0;
          const issueQty = parseFloat(String(value)) || 0;
          return issueQty <= availableQty;
        }
      ),
  });

  interface FormValues {
    bag_no: BagOption | null;
    available_quantity: string | number;
    issue_quantity: string | number;
  }

  const formik = useFormik<FormValues>({
    initialValues: {
      bag_no: null,
      available_quantity: "",
      issue_quantity: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const issueQty = parseFloat(String(values.issue_quantity)) || 0;
      const availableQty = parseFloat(String(values.available_quantity)) || 0;
      // Check issue quantity <= available quantity
      if (issueQty > availableQty) {
        formik.setFieldError(
          "issue_quantity",
          `Issue Quantity (${issueQty}) cannot exceed Available Quantity (${availableQty})`
        );
        toast.error(
          `Issue Quantity (${issueQty}) cannot exceed Available Quantity (${availableQty})`
        );
        return;
      }

      if (!values.bag_no) {
        formik.setFieldError("bag_no", "Bag No. is required");
        return;
      }

      // Extract bag_no after null check so TypeScript knows it's not null
      const selectedBag = values.bag_no;

      // Check if bag is already added (when not in edit mode)
      if (editingBagId === null) {
        const existingBag = rows.find(
          (row) =>
            String(row.inventory_item_id) ===
            String(selectedBag.inventory_item_id)
        );

        if (existingBag) {
          // Bag already exists, prompt user to edit
          const result = await showConfirm(
            `Bag "${selectedBag.bag_no}" is already added. Would you like to edit the existing bag instead?`,
            "Bag Already Added",
            "Edit Existing",
            "Cancel"
          );

          if (result.isConfirmed) {
            // User wants to edit the existing bag
            handleEdit(existingBag);
            resetForm();
            return;
          } else {
            // User cancelled, don't add the bag
            resetForm();
            return;
          }
        }
      }

      // Check total issue quantity (excluding the row being edited)
      const rowsExcludingEdited = rows.filter((row) => row.id !== editingBagId);
      const currentTotal = rowsExcludingEdited.reduce(
        (sum, row) => sum + (parseFloat(String(row.issue_quantity)) || 0),
        0
      );
      const newTotal = currentTotal + issueQty;

      if (newTotal > quantity) {
        formik.setFieldError(
          "issue_quantity",
          `Total Issue Quantity (${newTotal.toFixed(
            2
          )}) cannot exceed ${quantity}`
        );
        toast.error(
          `Total Issue Quantity (${newTotal.toFixed(
            2
          )}) cannot exceed ${quantity}`
        );
        return;
      }

      if (editingBagId !== null) {
        // Check if the bag being edited to is already in another row
        const existingBag = rows.find(
          (row) =>
            row.id !== editingBagId &&
            String(row.inventory_item_id) ===
              String(selectedBag.inventory_item_id)
        );

        if (existingBag) {
          // Bag already exists in another row, prompt user
          const result = await showConfirm(
            `Bag "${selectedBag.bag_no}" is already added in another row. Would you like to edit that bag instead?`,
            "Bag Already Added",
            "Edit Existing",
            "Cancel"
          );

          if (result.isConfirmed) {
            // User wants to edit the existing bag, cancel current edit and switch to that bag
            // Find the bag option that matches the existing bag from bagOptions
            const bagOption = bagOptions.find(
              (option) =>
                String(option.bag_no) === String(existingBag.bag_no) &&
                String(option.inventory_item_id) === String(existingBag.inventory_item_id)
            );

            // Switch to editing the existing bag
            // Set editingBagId first, then set formik values
            setEditingBagId(existingBag.id);
            
            // Use the found bagOption or create a fallback that matches the structure
            const selectedBagOption = bagOption || {
              inventory_item_id: existingBag.inventory_item_id,
              bag_no: existingBag.bag_no,
              available_quantity: existingBag.available_quantity,
            } as BagOption;

            // Set all formik values atomically
            formik.setValues({
              bag_no: selectedBagOption,
              available_quantity: existingBag.available_quantity,
              issue_quantity: existingBag.issue_quantity,
            });
            
            // Force formik to recognize the field as touched so Autocomplete shows the value
            formik.setFieldTouched("bag_no", true);
            return;
          } else {
            // User cancelled, keep current edit but don't update
            return;
          }
        }

        // Update existing row
        const updatedRows = rows.map((row) =>
          row.id === editingBagId
            ? {
                id: editingBagId,
                inventory_item_id: selectedBag.inventory_item_id,
                bag_no: selectedBag.bag_no,
                available_quantity: availableQty,
                issue_quantity: issueQty,
              }
            : row
        );
        setRows(updatedRows);
        setEditingBagId(null);
      } else {
        // Add new row
        const newRow: BagRow = {
          id: Date.now().toString(),
          inventory_item_id: selectedBag.inventory_item_id,
          bag_no: selectedBag.bag_no,
          available_quantity: availableQty,
          issue_quantity: issueQty,
        };
        setRows([...rows, newRow]);
      }

      resetForm();
    },
  });

  const handleDelete = (id: string) => {
    setRows(rows.filter((row) => row.id !== id));
    if (editingBagId === id) {
      setEditingBagId(null);
      formik.resetForm();
    }
  };

  const handleEdit = (row: BagRow) => {
    setEditingBagId(row.id);
    // Find the bag option that matches this row
    const bagOption = bagOptions.find(
      (option) =>
        option.bag_no === row.bag_no &&
        option.inventory_item_id === row.inventory_item_id
    ) || {
      inventory_item_id: row.inventory_item_id,
      bag_no: row.bag_no,
      available_quantity: row.available_quantity,
    };

    formik.setFieldValue("bag_no", bagOption);
    formik.setFieldValue("available_quantity", row.available_quantity);
    formik.setFieldValue("issue_quantity", row.issue_quantity);
  };

  const handleCancelEdit = () => {
    setEditingBagId(null);
    formik.resetForm();
  };

  const handleSave = () => {
    const totalIssueQuantity = rows.reduce(
      (sum, row) => sum + (parseFloat(String(row.issue_quantity)) || 0),
      0
    );

    if (totalIssueQuantity > quantity) {
      toast.error(
        `Total Issue Quantity (${totalIssueQuantity}) cannot exceed ${quantity}`
      );
      return;
    }

    if (rows.length === 0) {
      toast.error("Please add at least one bag");
      return;
    }

    // Update bagQuantityValidation state
    if (setBagQuantityValidation) {
      // First, remove old bags from current item if editing
      // Create a copy to avoid mutating the original
      let updatedBagValidation = bagQuantityValidation.map((bag) => ({
        ...bag,
      }));

      if (
        editingItemId !== null &&
        currentItemBags &&
        currentItemBags.length > 0
      ) {
        // Aggregate current item's bag usage by inventory_item_id
        // This handles cases where the same bag appears multiple times in currentItemBags
        const currentItemBagUsage: { [key: string]: number } = {};
        currentItemBags.forEach((oldBag: any) => {
          const bagId = String(oldBag.inventory_item_id);
          const issueQty = parseFloat(String(oldBag.issue_quantity)) || 0;
          if (currentItemBagUsage[bagId]) {
            currentItemBagUsage[bagId] += issueQty;
          } else {
            currentItemBagUsage[bagId] = issueQty;
          }
        });

        // Remove current item's bag usage from validation state
        Object.entries(currentItemBagUsage).forEach(
          ([bagId, totalIssueQty]) => {
            const bagIndex = updatedBagValidation.findIndex(
              (b) => String(b.inventory_item_id) === bagId
            );
            if (bagIndex !== -1) {
              updatedBagValidation[bagIndex].issue_quantity -= totalIssueQty;
              // Remove if issue_quantity becomes 0 or negative
              if (updatedBagValidation[bagIndex].issue_quantity <= 0) {
                updatedBagValidation.splice(bagIndex, 1);
              }
            }
          }
        );
      }

      // Validate all bags before updating state
      // First, aggregate rows by inventory_item_id to handle cases where same bag appears multiple times
      const aggregatedRows: {
        [key: string]: { issue_quantity: number; available_quantity: number };
      } = {};
      rows.forEach((row) => {
        const bagId = String(row.inventory_item_id);
        const issueQty = parseFloat(String(row.issue_quantity)) || 0;
        const availableQty = parseFloat(String(row.available_quantity)) || 0;

        if (aggregatedRows[bagId]) {
          // Same bag appears multiple times, aggregate issue quantities
          aggregatedRows[bagId].issue_quantity += issueQty;
          // Keep the maximum available_quantity (should be same for same bag, but just in case)
          aggregatedRows[bagId].available_quantity = Math.max(
            aggregatedRows[bagId].available_quantity,
            availableQty
          );
        } else {
          aggregatedRows[bagId] = {
            issue_quantity: issueQty,
            available_quantity: availableQty,
          };
        }
      });

      // Now validate aggregated bags
      let validationError = false;
      for (const [bagId, bagData] of Object.entries(aggregatedRows)) {
        const issueQty = bagData.issue_quantity;
        let availableQty = bagData.available_quantity;

        // Find if bag already exists in validation state (this should only contain bags from OTHER items after removing current item's bags)
        const existingBagIndex = updatedBagValidation.findIndex(
          (b) => String(b.inventory_item_id) === bagId
        );

        if (existingBagIndex !== -1) {
          // Bag exists in OTHER items, check if adding current item's usage exceeds available
          const otherItemsUsage =
            updatedBagValidation[existingBagIndex].issue_quantity;
          const newTotalIssueQty = otherItemsUsage + issueQty;

          availableQty = availableQty + otherItemsUsage;

          // Validate that total issue quantity (other items + current item) doesn't exceed available quantity
          if (newTotalIssueQty > availableQty) {
            toast.error(
              `Bag is used for another item and the total issued quantity (${newTotalIssueQty.toFixed(
                2
              )}) is going greater than available quantity (${availableQty.toFixed(
                2
              )})`
            );
            validationError = true;
            break;
          }
        } else {
          // Bag doesn't exist in other items, validate that current item's usage doesn't exceed available
          if (issueQty > availableQty) {
            toast.error(
              `Issued quantity (${issueQty.toFixed(
                2
              )}) cannot exceed available quantity (${availableQty.toFixed(2)})`
            );
            validationError = true;
            break;
          }
        }
      }

      // If validation failed, don't update state
      if (validationError) {
        return;
      }

      // All validations passed, update the state using aggregated data
      for (const [bagId, bagData] of Object.entries(aggregatedRows)) {
        const issueQty = bagData.issue_quantity;
        const availableQty = bagData.available_quantity;

        const existingBagIndex = updatedBagValidation.findIndex(
          (b) => String(b.inventory_item_id) === bagId
        );

        if (existingBagIndex !== -1) {
          // Bag exists, append issue quantity
          updatedBagValidation[existingBagIndex].issue_quantity += issueQty;
        } else {
          // Bag doesn't exist, add new entry
          updatedBagValidation.push({
            inventory_item_id: bagId,
            available_quantity: availableQty,
            issue_quantity: issueQty,
          });
        }
      }

      setBagQuantityValidation(updatedBagValidation);
    }

    // Remove id field from bags before saving (id is only for internal modal use)
    const bagsToSave = rows.map(({ id, ...bag }) => bag);
    onSave(bagsToSave);
    handleClose();
  };

  const handleModalClose = () => {
    // Reset formik form when modal is closed
    formik.resetForm();
    setEditingBagId(null);
    // Reset rows to initial bags to discard any unsaved changes
    const bagsWithId: BagRow[] = (initialBags || []).map((bag, idx) => ({
      ...bag,
      id: `bag-${Date.now()}-${idx}`,
    }));
    setRows(bagsWithId);
    handleClose();
  };

  const totalIssueQuantity = rows.reduce(
    (sum, row) => sum + (parseFloat(String(row.issue_quantity)) || 0),
    0
  );
  const remainingQuantity = quantity - totalIssueQuantity;

  // Filter out fully utilized bags (where issue_quantity >= available_quantity)
  // A bag is fully utilized if it's already used in other items and the total issue_quantity equals or exceeds available_quantity
  const filteredBagOptions = bagOptions.filter((bagOption) => {
    // Check if this bag is already in current rows (being edited in this modal)
    const bagInCurrentRows = rows.filter(
      (row) =>
        String(row.inventory_item_id) === String(bagOption.inventory_item_id)
    );

    // If bag is already in current rows, check if it's fully utilized in the current modal
    if (bagInCurrentRows.length > 0) {
      // Calculate total issue quantity for this bag in current rows
      const currentModalUsage = bagInCurrentRows.reduce(
        (sum, row) => sum + (parseFloat(String(row.issue_quantity)) || 0),
        0
      );

      // Check if any of the rows with this bag are being edited
      const isEditingThisBag =
        editingBagId !== null &&
        bagInCurrentRows.some((row) => row.id === editingBagId);

      // If the bag is fully utilized in the current modal (issue_quantity >= available_quantity),
      // and we're not editing one of those rows, exclude it
      if (
        currentModalUsage >= parseFloat(String(bagOption.available_quantity))
      ) {
        if (!isEditingThisBag) {
          return false; // Bag is fully utilized in current modal and not being edited
        }
        // If we're editing this bag, allow it (user might reduce the quantity)
        return true;
      }

      // If bag is in current rows and not fully utilized, show it
      return true;
    }

    // If bag is not in current rows, check if it's fully utilized in other items
    const existingBag = bagQuantityValidation.find(
      (b) => String(b.inventory_item_id) === String(bagOption.inventory_item_id)
    );

    if (existingBag) {
      // When editing, bagQuantityValidation includes current item's bags
      // We need to subtract current item's usage to check if bag is fully utilized by OTHER items
      let otherItemsUsage = existingBag.issue_quantity;

      if (
        editingItemId !== null &&
        currentItemBags &&
        currentItemBags.length > 0
      ) {
        // Find current item's usage for this bag
        const currentItemBag = currentItemBags.find(
          (bag: any) =>
            String(bag.inventory_item_id) ===
            String(bagOption.inventory_item_id)
        );

        if (currentItemBag) {
          // Subtract current item's usage to get usage by other items only
          otherItemsUsage -=
            parseFloat(String(currentItemBag.issue_quantity)) || 0;
        }
      }

      // Check if the bag is fully utilized by OTHER items (not including current item)
      // If otherItemsUsage >= available_quantity, the bag is fully used by other items and should be excluded
      const isFullyUtilizedByOthers =
        otherItemsUsage >= existingBag.available_quantity;

      if (isFullyUtilizedByOthers) {
        // Bag is fully utilized by other items, exclude from dropdown
        return false;
      }

      // If not fully utilized, show it only if there's remaining available quantity
      const remainingAvailable =
        existingBag.available_quantity - otherItemsUsage;
      return remainingAvailable > 0;
    }

    // If bag is not in bagQuantityValidation, it's available
    return true;
  });

  return (
    <Modal show={show} onHide={handleModalClose} centered size="xl">
      <Modal.Header className="border-0">
        <Modal.Title className="fw-bold">Select Bags</Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleModalClose}
        />
      </Modal.Header>
      <hr />
      <Modal.Body>
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span style={{ fontSize: "0.875rem", color: "#212529" }}>
              Entered Quantity:
            </span>
            <span
              style={{
                fontSize: "0.875rem",
                fontWeight: "500",
                color: "#212529",
              }}
            >
              {quantity}
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span style={{ fontSize: "0.875rem", color: "#212529" }}>
              Total Issue Quantity:
            </span>
            <span
              style={{
                fontSize: "0.875rem",
                fontWeight: "500",
                color: "#212529",
              }}
            >
              {totalIssueQuantity.toFixed(2)}
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span style={{ fontSize: "0.875rem", color: "#212529" }}>
              Remaining Quantity:
            </span>
            <span
              style={{
                fontSize: "0.875rem",
                fontWeight: "500",
                color: remainingQuantity >= 0 ? "#28a745" : "#dc3545",
              }}
            >
              {remainingQuantity.toFixed(2)}
            </span>
          </div>
        </div>

        <style>
          {`
                        .sticky-column {
                            position: relative;
                        }
                        .sticky-column::before {
                            content: '';
                            position: absolute;
                            left: 0;
                            top: 0;
                            bottom: 0;
                            width: 1px;
                            background-color: #dee2e6;
                            z-index: 12;
                        }
                        thead .sticky-column::before {
                            z-index: 13;
                        }
                    `}
        </style>
        <div className="mb-4">
          <Table hover responsive className="align-middle text-center">
            <thead>
              <tr>
                <th style={{ ...tableStyles.headerAlt, minWidth: "80px" }}>
                  Sr No.
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Bag No.
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Available Quantity
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Issue Quantity
                </th>
                <th
                  className="sticky-column"
                  style={{
                    ...tableStyles.headerAlt,
                    ...tableStyles.actionHeader,
                    minWidth: "100px",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Input Row */}
              <tr>
                <td style={tableStyles.cell}></td>
                <td style={tableStyles.cell}>
                  <Autocomplete
                    size="small"
                    options={filteredBagOptions || []}
                    autoHighlight
                    loading={loading}
                    disabled={loading || editingBagId !== null}
                    value={editingBagId !== null ? null : formik.values.bag_no}
                    onChange={async (_, val) => {
                      // Check if bag is already added (when not in edit mode)
                      if (val && editingBagId === null) {
                        const existingBag = rows.find(
                          (row) =>
                            String(row.inventory_item_id) ===
                            String(val.inventory_item_id)
                        );

                        if (existingBag) {
                          // Bag already exists, prompt user to edit
                          const result = await showConfirm(
                            `Bag "${val.bag_no}" is already added. Would you like to edit the existing bag instead?`,
                            "Bag Already Added",
                            "Edit Existing",
                            "Cancel"
                          );

                          if (result.isConfirmed) {
                            // User wants to edit the existing bag
                            handleEdit(existingBag);
                            // Clear the formik value since we're switching to edit mode
                            formik.setFieldValue("bag_no", null);
                            return;
                          } else {
                            // User cancelled, clear the selection
                            formik.setFieldValue("bag_no", null);
                            return;
                          }
                        }
                      }

                      formik.setFieldValue("bag_no", val);
                      if (val) {
                        formik.setFieldValue(
                          "available_quantity",
                          val?.available_quantity || ""
                        );

                        // Auto-fill issue quantity based on remaining required quantity
                        const rowsExcludingEdited = rows.filter(
                          (row) => row.id !== editingBagId
                        );
                        const currentTotal = rowsExcludingEdited.reduce(
                          (sum, row) =>
                            sum + (parseFloat(String(row.issue_quantity)) || 0),
                          0
                        );
                        const remainingQuantity = quantity - currentTotal;
                        const availableQty =
                          parseFloat(String(val?.available_quantity)) || 0;

                        // Set issue quantity to the minimum of remaining quantity and available quantity
                        const autoFillQty = Math.min(
                          remainingQuantity,
                          availableQty
                        );
                        formik.setFieldValue(
                          "issue_quantity",
                          autoFillQty > 0 ? autoFillQty.toFixed(2) : ""
                        );
                      } else {
                        formik.setFieldValue("available_quantity", "");
                        formik.setFieldValue("issue_quantity", "");
                      }
                      // Cancel edit mode when bag selection changes
                      if (editingBagId !== null) {
                        setEditingBagId(null);
                      }
                    }}
                    onBlur={() => formik.setFieldTouched("bag_no", true)}
                    getOptionLabel={(option: BagOption) =>
                      option
                        ? `${option.bag_no} (Available: ${option.available_quantity})`
                        : ""
                    }
                    renderOption={(props, option: BagOption) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) =>
                          `${opt?.bag_no || ""} (Available: ${
                            opt?.available_quantity || 0
                          })`
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Bag No"
                        error={
                          formik.touched.bag_no && Boolean(formik.errors.bag_no)
                        }
                        helperText={
                          formik.touched.bag_no &&
                          typeof formik.errors.bag_no === "string"
                            ? formik.errors.bag_no
                            : undefined
                        }
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    )}
                    ListboxProps={{
                      ...autocompleteDropdownStyles.ListboxProps,
                      style: {
                        maxHeight: "200px",
                        overflow: "auto",
                        zIndex: 1400,
                      },
                    }}
                    componentsProps={{
                      popper: {
                        style: {
                          zIndex: 1400,
                        },
                        modifiers: [
                          {
                            name: "preventOverflow",
                            enabled: true,
                          },
                        ],
                      },
                    }}
                    disablePortal={false}
                  />
                </td>
                <td style={tableStyles.cell}>
                  <TextField
                    size="small"
                    value={
                      editingBagId !== null
                        ? ""
                        : formik.values.available_quantity || ""
                    }
                    disabled
                    placeholder="Auto-filled from bag selection"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                </td>
                <td style={tableStyles.cell}>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Enter Issue Quantity"
                    value={
                      editingBagId !== null ? "" : formik.values.issue_quantity
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue("issue_quantity", value);
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      !!formik.errors.issue_quantity &&
                      formik.touched.issue_quantity
                    }
                    helperText={
                      formik.touched.issue_quantity &&
                      typeof formik.errors.issue_quantity === "string"
                        ? formik.errors.issue_quantity
                        : undefined
                    }
                    disabled={editingBagId !== null}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                </td>
                <td className="sticky-column" style={tableStyles.actionCell}>
                  <div style={tableStyles.actionButtonContainer}>
                    <SubmitButton
                      isSubmitting={false}
                      onClick={() => formik.handleSubmit()}
                      disabled={editingBagId !== null}
                    />
                  </div>
                </td>
              </tr>

              {/* Existing Rows */}
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={tableStyles.cell}
                    className="text-center py-2 text-muted"
                  >
                    No bags added. Please add at least one bag.
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => {
                  const isEditing = editingBagId === row.id;
                  const displayData = isEditing ? formik.values : row;

                  return (
                    <tr
                      key={row.id}
                      style={isEditing ? tableStyles.rowEditing : {}}
                    >
                      <td style={tableStyles.cell}>{idx + 1}</td>

                      {/* Bag No */}
                      <td style={tableStyles.cell}>
                        {isEditing ? (
                          <Autocomplete
                            size="small"
                            options={filteredBagOptions || []}
                            getOptionLabel={(opt: BagOption) =>
                              opt?.bag_no || ""
                            }
                            isOptionEqualToValue={(option, value) =>
                              String(option.inventory_item_id) === String(value.inventory_item_id) &&
                              String(option.bag_no) === String(value.bag_no)
                            }
                            value={
                              displayData.bag_no &&
                              typeof displayData.bag_no === "object" &&
                              "bag_no" in displayData.bag_no
                                ? bagOptions.find((b) => {
                                    const bagNo =
                                      displayData.bag_no as BagOption;
                                    return (
                                      String(b.bag_no) === String(bagNo.bag_no) &&
                                      String(b.inventory_item_id) ===
                                        String(bagNo.inventory_item_id)
                                    );
                                  }) || null
                                : null
                            }
                            onChange={async (_, value) => {
                              if (value) {
                                // Check if bag is already added in another row (excluding the row being edited)
                                const existingBag = rows.find(
                                  (row) =>
                                    row.id !== editingBagId &&
                                    String(row.inventory_item_id) ===
                                      String(value.inventory_item_id)
                                );

                                if (existingBag) {
                                  // Bag already exists in another row, prompt user
                                  const result = await showConfirm(
                                    `Bag "${value.bag_no}" is already added in another row. Would you like to edit that bag instead?`,
                                    "Bag Already Added",
                                    "Edit Existing",
                                    "Cancel"
                                  );

                                  if (result.isConfirmed) {
                                    // User wants to edit the existing bag, switch to that bag
                                    // Find the bag option that matches the existing bag from bagOptions
                                    const bagOption = bagOptions.find(
                                      (option) =>
                                        String(option.bag_no) === String(existingBag.bag_no) &&
                                        String(option.inventory_item_id) === String(existingBag.inventory_item_id)
                                    );

                                    // Switch to editing the existing bag
                                    // Set editingBagId first, then set formik values
                                    setEditingBagId(existingBag.id);
                                    
                                    // Use the found bagOption or create a fallback that matches the structure
                                    const selectedBagOption = bagOption || {
                                      inventory_item_id: existingBag.inventory_item_id,
                                      bag_no: existingBag.bag_no,
                                      available_quantity: existingBag.available_quantity,
                                    } as BagOption;

                                    // Set all formik values atomically
                                    formik.setValues({
                                      bag_no: selectedBagOption,
                                      available_quantity: existingBag.available_quantity,
                                      issue_quantity: existingBag.issue_quantity,
                                    });
                                    
                                    // Force formik to recognize the field as touched so Autocomplete shows the value
                                    formik.setFieldTouched("bag_no", true);
                                    return;
                                  } else {
                                    // User cancelled, keep current bag selection
                                    return;
                                  }
                                }

                                formik.setFieldValue("bag_no", value);
                                formik.setFieldValue(
                                  "available_quantity",
                                  value.available_quantity || ""
                                );

                                // Auto-fill issue quantity based on remaining required quantity
                                const rowsExcludingEdited = rows.filter(
                                  (row) => row.id !== editingBagId
                                );
                                const currentTotal = rowsExcludingEdited.reduce(
                                  (sum, row) =>
                                    sum +
                                    (parseFloat(String(row.issue_quantity)) ||
                                      0),
                                  0
                                );
                                const remainingQuantity =
                                  quantity - currentTotal;
                                const availableQty =
                                  parseFloat(
                                    String(value.available_quantity)
                                  ) || 0;

                                // Set issue quantity to the minimum of remaining quantity and available quantity
                                const autoFillQty = Math.min(
                                  remainingQuantity,
                                  availableQty
                                );
                                formik.setFieldValue(
                                  "issue_quantity",
                                  autoFillQty > 0 ? autoFillQty.toFixed(2) : ""
                                );
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                sx={{
                                  ...autocompleteDropdownStyles.inputSx,
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "6px 12px",
                                    height: "20px",
                                  },
                                }}
                              />
                            )}
                            renderOption={(props, option) =>
                              autocompleteDropdownStyles.renderOption(
                                props,
                                option,
                                (opt) => opt?.bag_no || ""
                              )
                            }
                            ListboxProps={{
                              ...autocompleteDropdownStyles.ListboxProps,
                              style: {
                                maxHeight: "200px",
                                overflow: "auto",
                                zIndex: 1400,
                              },
                            }}
                            componentsProps={{
                              popper: {
                                style: {
                                  zIndex: 1400,
                                },
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    enabled: true,
                                  },
                                ],
                              },
                            }}
                            disablePortal={false}
                          />
                        ) : (
                          <span>{row.bag_no || "-"}</span>
                        )}
                      </td>

                      {/* Available Quantity */}
                      <td style={tableStyles.cell}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={displayData.available_quantity || ""}
                            disabled
                            fullWidth
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                              "& .MuiInputBase-input": {
                                padding: "6px 12px",
                                height: "20px",
                              },
                            }}
                          />
                        ) : (
                          <span>{row.available_quantity || "-"}</span>
                        )}
                      </td>

                      {/* Issue Quantity */}
                      <td style={tableStyles.cell}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={displayData.issue_quantity || ""}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "issue_quantity",
                                e.target.value
                              )
                            }
                            fullWidth
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                              "& .MuiInputBase-input": {
                                padding: "6px 12px",
                                height: "20px",
                              },
                            }}
                          />
                        ) : (
                          <span>{row.issue_quantity || "-"}</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td
                        style={
                          isEditing
                            ? tableStyles.actionCellEditing
                            : tableStyles.actionCell
                        }
                        className="sticky-column"
                      >
                        {isEditing ? (
                          <SaveCancelButtons
                            onSave={() => formik.handleSubmit()}
                            onCancel={handleCancelEdit}
                            containerStyle={tableStyles.actionButtonContainer}
                          />
                        ) : (
                          <div style={tableStyles.actionButtonContainer}>
                            <EditButton
                              onClick={() => handleEdit(row)}
                              iconClassName="ph-pencil"
                            />
                            <DeleteButton
                              onClick={() => handleDelete(row.id)}
                              iconClassName="ph-trash"
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-center gap-3 py-3">
          <CancelBackButton
            onClick={handleModalClose}
            label="Cancel"
          />
          <SubmitButton
            isSubmitting={false}
            onClick={handleSave}
            labels={{
              create: "Save",
            }}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default QuotationBagSelectionModal;
